export const USER_TIERS = Object.freeze({
  FREE: 'free',
  PREMIUM: 'premium'
});

export const PREMIUM_PLAN_NAME = 'Game Master';
export const PREMIUM_PRICE_LABEL = '$9.99/month';
export const UPGRADE_ROUTE = '/upgrade';

// date_of_change: 2026-04-12 - Stripe checkout URL for premium upgrade
export const STRIPE_CHECKOUT_URL = 'https://checkout.stripe.com/c/pay/cs_live_a1Xr9OrSahTEVDdzoz1aone6DW5WlYRKlr6rCen1HGigsmq9pxDdrT5OWS#fidnandhYHdWcXxpYCc%2FJ2FgY2RwaXEnKSdkdWxOYHwnPyd1blppbHNgWjA0VmRDakxCYEpnNXRgc2ttd3FfSmJvR2FwU20zclJtSWFRQjVHN1VcYDRcSzc9QUdAbXZ0Sn1QS0hCaD1LbEtgZEZUSE49XExDaWEzPGNqXGhXTFRQZ0FxNTVfZEkybGRWZCcpJ2N3amhWYHdzYHcnP3F3cGApJ2dkZm5id2pwa2FGamlqdyc%2FJyZjY2NjY2MnKSdpZHxqcHFRfHVgJz8ndmxrYmlgWmxxYGgnKSdga2RnaWBVaWRmYG1qaWFgd3YnP3F3cGB4JSUl';

export const SESSION_INDEX_STORAGE_KEY = 'ascendant_battle_sessions_index';
export const UNIFIED_ENTITY_STORAGE_KEY = 'ascendant_unified_entities';
export const CUSTOM_ASSET_STORAGE_KEY = 'ascendant_custom_assets';

export const ENTITLEMENT_CONFIG = Object.freeze({
  [USER_TIERS.FREE]: Object.freeze({
    userTier: USER_TIERS.FREE,
    planName: 'Free',
    headline: 'Start Playing Immediately',
    limits: Object.freeze({
      savedSessions: 1,
      sessionExpirationDays: 30,
      customCharacters: 2,
      customMonsters: 2,
      assetCount: 10,
      assetStorageBytes: 'unlimited'
    }),
    features: Object.freeze({
      fullCombatSystem: true,
      multiplayer: true,
      fogOfWar: true,
      gmToolsBasic: true,
      encounterPresets: false,
      customRules: false,
      savedRuleConfigurations: false,
      multiSessionContinuity: false
    })
  }),
  [USER_TIERS.PREMIUM]: Object.freeze({
    userTier: USER_TIERS.PREMIUM,
    planName: PREMIUM_PLAN_NAME,
    headline: 'Run the whole campaign without rails',
    limits: Object.freeze({
      savedSessions: 'unlimited',
      sessionExpirationDays: null,
      customCharacters: 'unlimited',
      customMonsters: 'unlimited',
      assetCount: 'unlimited',
      assetStorageBytes: 1_000_000_000
    }),
    features: Object.freeze({
      fullCombatSystem: true,
      multiplayer: true,
      fogOfWar: true,
      gmToolsBasic: true,
      encounterPresets: true,
      customRules: true,
      savedRuleConfigurations: true,
      multiSessionContinuity: true
    })
  })
});

export const PREMIUM_FEATURE_COPY = Object.freeze({
  extraSaveSlots: {
    name: 'More Save Slots',
    cta: 'Upgrade for endless campaign saves'
  },
  encounterPresets: {
    name: 'Encounter Presets',
    cta: 'Upgrade to bank reusable encounters'
  },
  customRules: {
    name: 'Custom Rules',
    cta: 'Upgrade to forge house rules'
  },
  customCharacters: {
    name: 'Expanded Hero Roster',
    cta: 'Upgrade for unlimited custom heroes'
  },
  customMonsters: {
    name: 'Expanded Monster Vault',
    cta: 'Upgrade for unlimited custom monsters'
  },
  assetLibrary: {
    name: 'Expanded Asset Vault',
    cta: 'Upgrade for a bigger map and token vault'
  }
});

export function normalizeTier(value) {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === USER_TIERS.PREMIUM ? USER_TIERS.PREMIUM : USER_TIERS.FREE;
}

export function getTierConfig(value) {
  const tier = normalizeTier(value);
  return ENTITLEMENT_CONFIG[tier];
}

export function isPremiumTier(value) {
  return normalizeTier(value) === USER_TIERS.PREMIUM;
}

export function toFiniteLimit(limit) {
  return limit === 'unlimited' ? Number.POSITIVE_INFINITY : Number(limit || 0);
}

export function formatBytes(bytes) {
  const safeBytes = Number(bytes || 0);
  if (safeBytes >= 1024 * 1024 * 1024) {
    return `${(safeBytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }
  if (safeBytes >= 1024 * 1024) {
    return `${(safeBytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (safeBytes >= 1024) {
    return `${Math.round(safeBytes / 1024)} KB`;
  }
  return `${safeBytes} B`;
}

export function buildUsageStatus(limit, used) {
  const finiteLimit = toFiniteLimit(limit);
  const safeUsed = Number(used || 0);
  const remaining = Number.isFinite(finiteLimit)
    ? Math.max(finiteLimit - safeUsed, 0)
    : Number.POSITIVE_INFINITY;

  return {
    limit,
    used: safeUsed,
    remaining,
    atLimit: Number.isFinite(finiteLimit) ? safeUsed >= finiteLimit : false,
    isUnlimited: !Number.isFinite(finiteLimit)
  };
}

export function getSessionExpirationDate(session) {
  const raw = session?.expiresAt || session?.expirationTimestamp || null;
  if (!raw) {
    return null;
  }
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function isSessionExpired(session, now = new Date()) {
  const expiry = getSessionExpirationDate(session);
  if (!expiry) {
    return false;
  }
  return expiry.getTime() <= now.getTime();
}
